import React from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';

const ALERT_TYPES = [
  { value: 'campaign_ending_soon', label: 'Campaign Ending Soon', desc: 'Get notified when campaigns are about to end' },
  { value: 'revenue_increase', label: 'Revenue Threshold', desc: 'Alert when revenue reaches a specific amount' },
  { value: 'campaign_started', label: 'New Campaign Created', desc: 'Get notified when new campaigns are created' },
  { value: 'budget_threshold', label: 'Budget Threshold', desc: 'Alert when campaign budget exceeds a limit' },
  { value: 'new_client', label: 'New Client Added', desc: 'Get notified when new clients are added' },
];

function AlertConditions({ alertType, conditions, updateCondition, clients }) {
  if (alertType === 'campaign_ending_soon') {
    return (
      <div className="space-y-2 bg-zinc-50 border border-zinc-200 p-4">
        <Label className="text-xs font-medium tracking-[0.1em] uppercase text-zinc-500">Days Before End Date</Label>
        <Input type="number" value={conditions.days_before || ''} onChange={(e) => updateCondition('days_before', parseInt(e.target.value) || 0)} placeholder="e.g., 3" className="rounded-none font-mono" data-testid="condition-days-before" />
        <p className="text-xs text-zinc-400">Alert when a campaign's end date is within this many days</p>
      </div>
    );
  }
  if (alertType === 'revenue_increase') {
    return (
      <div className="space-y-3 bg-zinc-50 border border-zinc-200 p-4">
        <div className="space-y-2">
          <Label className="text-xs font-medium tracking-[0.1em] uppercase text-zinc-500">Revenue Threshold ($)</Label>
          <Input type="number" value={conditions.threshold_amount || ''} onChange={(e) => updateCondition('threshold_amount', parseFloat(e.target.value) || 0)} placeholder="e.g., 50000" className="rounded-none font-mono" data-testid="condition-threshold" />
        </div>
        <div className="space-y-2">
          <Label className="text-xs font-medium tracking-[0.1em] uppercase text-zinc-500">Specific Client (Optional)</Label>
          <Select value={conditions.client_id || 'all'} onValueChange={(val) => updateCondition('client_id', val === 'all' ? '' : val)}>
            <SelectTrigger className="rounded-none" data-testid="condition-client-select"><SelectValue placeholder="All clients" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Clients</SelectItem>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} — {c.company}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
    );
  }
  if (alertType === 'campaign_started') {
    return (
      <div className="space-y-2 bg-zinc-50 border border-zinc-200 p-4">
        <Label className="text-xs font-medium tracking-[0.1em] uppercase text-zinc-500">Lookback Period (Days)</Label>
        <Input type="number" value={conditions.days_lookback || ''} onChange={(e) => updateCondition('days_lookback', parseInt(e.target.value) || 0)} placeholder="e.g., 1" className="rounded-none font-mono" data-testid="condition-days-lookback" />
        <p className="text-xs text-zinc-400">Check for campaigns created within this many days</p>
      </div>
    );
  }
  if (alertType === 'budget_threshold') {
    return (
      <div className="space-y-2 bg-zinc-50 border border-zinc-200 p-4">
        <Label className="text-xs font-medium tracking-[0.1em] uppercase text-zinc-500">Budget Threshold ($)</Label>
        <Input type="number" value={conditions.threshold_amount || ''} onChange={(e) => updateCondition('threshold_amount', parseFloat(e.target.value) || 0)} placeholder="e.g., 100000" className="rounded-none font-mono" data-testid="condition-budget-threshold" />
        <p className="text-xs text-zinc-400">Alert when any campaign budget exceeds this amount</p>
      </div>
    );
  }
  if (alertType === 'new_client') {
    return (
      <div className="space-y-2 bg-zinc-50 border border-zinc-200 p-4">
        <Label className="text-xs font-medium tracking-[0.1em] uppercase text-zinc-500">Lookback Period (Days)</Label>
        <Input type="number" value={conditions.days_lookback || ''} onChange={(e) => updateCondition('days_lookback', parseInt(e.target.value) || 0)} placeholder="e.g., 1" className="rounded-none font-mono" data-testid="condition-client-lookback" />
        <p className="text-xs text-zinc-400">Check for clients added within this many days</p>
      </div>
    );
  }
  return null;
}

export function AlertRuleFormDialog({ open, onOpenChange, editing, form, setForm, onSave, saving, clients }) {
  const updateCondition = (key, value) => {
    setForm(f => ({ ...f, conditions: { ...f.conditions, [key]: value } }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg rounded-none border-zinc-200 max-h-[90vh] overflow-y-auto" data-testid="alert-dialog">
        <DialogHeader><DialogTitle className="text-xl font-bold tracking-tight">{editing ? 'Edit Alert Rule' : 'New Alert Rule'}</DialogTitle></DialogHeader>
        <div className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label className="text-xs font-medium tracking-[0.1em] uppercase text-zinc-500">Rule Name</Label>
            <Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} placeholder="e.g., Campaign expiry warning" className="rounded-none" data-testid="alert-name-input" required />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-medium tracking-[0.1em] uppercase text-zinc-500">Alert Type</Label>
            <Select value={form.alert_type} onValueChange={(val) => setForm({...form, alert_type: val, conditions: {}})}>
              <SelectTrigger className="rounded-none" data-testid="alert-type-select"><SelectValue placeholder="Select alert type" /></SelectTrigger>
              <SelectContent>{ALERT_TYPES.map((t) => <SelectItem key={t.value} value={t.value}><span className="font-medium">{t.label}</span></SelectItem>)}</SelectContent>
            </Select>
          </div>
          <AlertConditions alertType={form.alert_type} conditions={form.conditions} updateCondition={updateCondition} clients={clients} />
          <div className="space-y-2">
            <Label className="text-xs font-medium tracking-[0.1em] uppercase text-zinc-500">Recipients (comma-separated emails)</Label>
            <Input value={form.recipients} onChange={(e) => setForm({...form, recipients: e.target.value})} placeholder="email1@example.com" className="rounded-none" data-testid="alert-recipients-input" />
          </div>
          <div className="flex items-center justify-between bg-zinc-50 border border-zinc-200 p-4">
            <div><div className="text-sm font-medium text-zinc-900">Send to myself</div><div className="text-xs text-zinc-400">Include your own email</div></div>
            <Switch checked={form.send_to_self} onCheckedChange={(val) => setForm({...form, send_to_self: val})} data-testid="alert-self-switch" />
          </div>
          <div className="flex items-center justify-between bg-zinc-50 border border-zinc-200 p-4">
            <div><div className="text-sm font-medium text-zinc-900">Enabled</div><div className="text-xs text-zinc-400">Active rules are evaluated when alerts run</div></div>
            <Switch checked={form.enabled} onCheckedChange={(val) => setForm({...form, enabled: val})} data-testid="alert-enabled-switch" />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-none border-zinc-300" data-testid="alert-cancel-button">Cancel</Button>
            <Button onClick={onSave} disabled={saving || !form.name || !form.alert_type} className="rounded-none bg-black text-white hover:bg-zinc-800" data-testid="alert-save-button">{saving ? 'Saving...' : editing ? 'Update' : 'Create Rule'}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export { ALERT_TYPES };
